import React, { useLayoutEffect, useRef, useState } from "react";

import { dasharray, donutGeometry, percentFromAmounts, percentLabel } from "../donutMath";
import { count, rupeesWhole } from "../../Insights/format";

const RING = 14;
const TRACK = "#e5e7eb";
const ACCOMPLISHED = "#fb310d";

const useSquareSize = (fallback) => {
  const ref = useRef(null);
  const [size, setSize] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const read = () => setSize(node.clientWidth);
    read();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read);
      return () => window.removeEventListener("resize", read);
    }

    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, size || fallback];
};

export default function CompletionDonut({ accomplished, total, orders, delivered, canceled, percent }) {
  const [ringRef, size] = useSquareSize(160);
  const { center, radius, circumference } = donutGeometry({ size, stroke: RING });

  const given = percent === null || percent === undefined ? null : Number(percent);
  const share = given !== null && Number.isFinite(given) ? given : percentFromAmounts(accomplished, total);
  const hasShare = share !== null;

  return (
    <div className="dash-donut-block">
      <div className="dash-donut" ref={ringRef}>
        <svg width={size} height={size} aria-hidden="true">
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={TRACK}
            strokeWidth={RING}
          />
          {hasShare && (
            <circle
              data-testid="donut-arc"
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={ACCOMPLISHED}
              strokeWidth={RING}
              strokeDasharray={dasharray(share, circumference)}
              transform={`rotate(-90 ${center} ${center})`}
            />
          )}
        </svg>
        <div className="dash-donut-centre">
          {hasShare ? (
            <>
              <span className="dash-donut-pct">{percentLabel(share)}</span>
              <span className="dash-donut-sub">Completion</span>
            </>
          ) : (
            <span className="dash-donut-none">No orders yet</span>
          )}
        </div>
      </div>

      <div className="dash-donut-data">
        <dl className="dash-donut-card dash-donut-revenue">
          <div className="dash-count">
            <dt>
              <span className="dash-chip" style={{ background: "#fb310d" }} />
              Delivered Amount
            </dt>
            <dd>{rupeesWhole(accomplished)}</dd>
          </div>
          <div className="dash-count">
            <dt>
              <span className="dash-chip" style={{ background: "#0f172a" }} />
              Total Amount
            </dt>
            <dd>{rupeesWhole(total)}</dd>
          </div>
        </dl>

        <dl className="dash-donut-card dash-donut-counts">
          <div className="dash-count">
            <dt>
              <span className="dash-chip" style={{ background: "#0f172a" }} />
              Orders
            </dt>
            <dd>{count(orders)}</dd>
          </div>
          <div className="dash-count">
            <dt>
              <span className="dash-chip" style={{ background: "#2f7a45" }} />
              Delivered
            </dt>
            <dd>{count(delivered)}</dd>
          </div>
          <div className="dash-count">
            <dt>
              <span className="dash-chip" style={{ background: "#9ca3af" }} />
              Canceled
            </dt>
            <dd>{count(canceled)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
